/**
 * RedeemCodePage - Bundle Code Redemption
 * 
 * Flow:
 * 1. User enters code (or arrives via /redeem?code=XXX)
 * 2. Code validated → shows book info
 * 3. User enters name + email
 * 4. Account created with 30-day trial + book access
 * 5. PDF download link shown
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BookOpen,
  CheckCircle2,
  Download,
  ArrowRight,
  Loader2,
  AlertCircle,
  Gift,
  Calendar,
  Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RedeemCodePage = () => {
  const [searchParams] = useSearchParams();
  const initialCode = searchParams.get('code') || '';
  
  const [code, setCode] = useState(initialCode);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  
  const [step, setStep] = useState(initialCode ? 'validating' : 'enter_code'); // enter_code, validating, valid, enter_details, redeeming, success
  const [codeInfo, setCodeInfo] = useState(null);
  const [redemptionResult, setRedemptionResult] = useState(null);
  const [error, setError] = useState('');
  
  // Auto-validate if code in URL
  useEffect(() => {
    if (initialCode) {
      validateCode(initialCode);
    }
  }, [initialCode]);

  const validateCode = async (codeToValidate) => {
    setStep('validating');
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/bundle/validate/${codeToValidate.toUpperCase()}`);
      const data = await response.json();
      
      if (data.valid) {
        setCodeInfo(data);
        setStep('valid');
      } else {
        setError(data.message);
        setStep('enter_code');
      }
    } catch (err) {
      setError('Failed to validate code. Please try again.');
      setStep('enter_code');
    }
  };

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    if (code.trim()) {
      validateCode(code.trim());
    }
  };

  const handleRedeemSubmit = async (e) => {
    e.preventDefault();
    setStep('redeeming');
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/bundle/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: code.toUpperCase(),
          email: email.trim(),
          name: name.trim()
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setRedemptionResult(data);
        setStep('success');
      } else {
        setError(data.detail || data.message || 'Failed to redeem code');
        setStep('valid'); // Go back to details form
      }
    } catch (err) {
      setError('Failed to redeem code. Please try again.');
      setStep('valid');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
      </div>
      
      <div className="relative max-w-lg mx-auto px-6 py-16">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6">
            <span className="text-2xl font-bold text-white">30 Day Skills</span>
          </Link>
          <h1 className="text-3xl font-bold text-white mb-2">
            Redeem Your Code
          </h1>
          <p className="text-zinc-400">
            Unlock your book and 30 days of Companion access
          </p>
        </div>

        {/* Step: Enter Code */}
        {step === 'enter_code' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <form onSubmit={handleCodeSubmit} className="bg-white/10 backdrop-blur border border-white/20 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-violet-500/20 rounded-full flex items-center justify-center">
                  <Gift className="w-5 h-5 text-violet-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">Enter Your Code</h2>
                  <p className="text-zinc-400 text-sm">Format: 30DS1-XXXXXX</p>
                </div>
              </div>
              
              <Input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="30DS1-XXXXXX"
                className="h-14 text-lg text-center font-mono bg-white/10 border-white/20 text-white placeholder:text-zinc-500 mb-4"
                data-testid="redeem-code-input"
              />
              
              {error && (
                <div className="flex items-center gap-2 text-red-400 text-sm mb-4">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}
              
              <Button
                type="submit"
                disabled={!code.trim()}
                className="w-full h-12 bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="validate-code-btn"
              >
                Validate Code
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
          </motion.div>
        )}

        {/* Step: Validating */}
        {step === 'validating' && (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 text-violet-400 animate-spin mx-auto mb-4" />
            <p className="text-zinc-400">Validating your code...</p>
          </div>
        )}

        {/* Step: Valid - Show book info and form */}
        {step === 'valid' && codeInfo && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            {/* Code valid banner */}
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-400" />
              <div>
                <p className="text-green-400 font-medium">Valid Code!</p>
                <p className="text-zinc-400 text-sm">{codeInfo.message}</p>
              </div>
            </div>
            
            {/* What you'll get */}
            <div className="bg-white/10 backdrop-blur border border-white/20 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-violet-400" />
                What You'll Get
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <BookOpen className="w-5 h-5 text-violet-400 mt-0.5" />
                  <div>
                    <p className="text-white font-medium">{codeInfo.book_title}</p>
                    <p className="text-zinc-400 text-sm">Complete PDF with QR codes for daily exercises</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-violet-400 mt-0.5" />
                  <div>
                    <p className="text-white font-medium">{codeInfo.trial_days} Days of Companion Access</p>
                    <p className="text-zinc-400 text-sm">AI-powered reflection and leadership coaching</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Details form */}
            <form onSubmit={handleRedeemSubmit} className="bg-white/10 backdrop-blur border border-white/20 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Create Your Account</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Your Name</label>
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter your name"
                    required
                    className="h-12 bg-white/10 border-white/20 text-white placeholder:text-zinc-500"
                    data-testid="redeem-name-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Email Address</label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="h-12 bg-white/10 border-white/20 text-white placeholder:text-zinc-500"
                    data-testid="redeem-email-input"
                  />
                  <p className="text-zinc-500 text-xs mt-1">This code will be tied to this email</p>
                </div>
                
                {error && (
                  <div className="flex items-center gap-2 text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                )}
                
                <Button
                  type="submit"
                  disabled={!name.trim() || !email.trim()}
                  className="w-full h-12 bg-violet-600 hover:bg-violet-700 text-white"
                  data-testid="redeem-submit-btn"
                >
                  Unlock My Access
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Step: Redeeming */}
        {step === 'redeeming' && (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 text-violet-400 animate-spin mx-auto mb-4" />
            <p className="text-zinc-400">Creating your account...</p>
          </div>
        )}

        {/* Step: Success */}
        {step === 'success' && redemptionResult && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="space-y-4"
          >
            {/* Success banner */}
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">You're All Set!</h2>
              <p className="text-zinc-400">{redemptionResult.message}</p>
            </div>
            
            {/* Download section */}
            <div className="bg-white/10 backdrop-blur border border-white/20 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Download className="w-5 h-5 text-violet-400" />
                Download Your Book
              </h3>
              
              <p className="text-zinc-400 text-sm mb-4">
                Click below to download <strong className="text-white">{redemptionResult.book_title}</strong>.
                You can also access this from your dashboard anytime.
              </p>
              
              <a
                href={`${API_URL}${redemptionResult.download_url}`}
                className="inline-flex items-center gap-2 w-full justify-center bg-violet-600 hover:bg-violet-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                data-testid="download-book-btn"
              >
                <Download className="w-5 h-5" />
                Download PDF
              </a>
            </div>
            
            {/* Next steps */}
            <div className="bg-white/10 backdrop-blur border border-white/20 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">What's Next?</h3>
              
              <ol className="space-y-3 text-zinc-300 text-sm">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-violet-500/20 text-violet-400 rounded-full flex items-center justify-center text-xs font-bold shrink-0">1</span>
                  <span>Check your email for a welcome message with login instructions</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-violet-500/20 text-violet-400 rounded-full flex items-center justify-center text-xs font-bold shrink-0">2</span>
                  <span>Start reading Day 1 and scan the QR code to begin your first reflection</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-violet-500/20 text-violet-400 rounded-full flex items-center justify-center text-xs font-bold shrink-0">3</span>
                  <span>Spend 5 minutes each day reflecting with the Companion</span>
                </li>
              </ol>
              
              <div className="mt-6 pt-4 border-t border-white/10">
                <Link to="/login">
                  <Button className="w-full bg-white text-black hover:bg-zinc-200">
                    Go to Dashboard
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Trial info */}
            <div className="text-center text-zinc-500 text-sm">
              Your {redemptionResult.trial_days}-day trial started. No credit card required.
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default RedeemCodePage;
